import { put, call, takeLatest } from "redux-saga/effects";
import * as API from "services";
import * as Actions from "actions";
import { EditorHome } from "constants/Actions";
import { groupByGroupId } from "functions";
import { $Message, $MessageBox } from "us.common/components";
import { i18n } from "us.helper";
import { getSortedTemplateData } from "components/EditorHome/Components/TemplateContent/Functions";
import { SortingOptions, SortingOrder } from "components/EditorHome/Constants";

const { editorHome, templatesHistory } = Actions;

const { template, texts, categoryDataAdapters, category, settings } =
  API.editorHome;
const { templates } = API.templatesHistory;

const {
  GET_ALL_TEMPLATE_RULES,
  GET_ALL_TEXT_DATA,
  GET_DATA_ADAPTERS,
  SAVE_TEMPLATE_CATEGORY,
  UPDATE_CATEGORY_DISPLAY_NAME,
  GET_CATEGORY_DETAILS,
  GET_ALL_SETTINGS,
  UPDATE_SETTINGS,
  ADD_REMOVE_FROM_MESSAGE_TEMPLATES,
} = EditorHome;

const editorHomeSagas = {
  template: {
    getAllRules: function* (action: any) {
      try {
        const { data, status } = yield call(template.getAllRules);
        if (status === 200) {
          yield put(editorHome.templateRules.success(data));
        } else {
          yield put(editorHome.templateRules.fail(data));
        }
      } catch (error) {
        yield put(editorHome.templateRules.fail(error as any));
      }
    },
    addRemoveFromMessageTemplates: function* (action: any) {
      try {
        const { payload, refreshAll } = action?.payload.data ?? {};
        if (payload?.IsDelete) {
          $Message.loading(i18n.t("US.USC.TEMPLATEEDITOR:MESSAGES.REMOVING_FROM_MESSAGE_TEMPLATE_IS_IN_PROGRESS") as string, 0);
        }
        else{
          $Message.loading(i18n.t("US.USC.TEMPLATEEDITOR:MESSAGES.ADDING_TO_MESSAGE_TEMPLATE_IS_IN_PROGRESS") as string, 0);
        }
        const { data, status } = yield call(
          template.addRemoveFromMessageTemplates,
          payload
        );
        if (status === 201) {
          if (refreshAll) {
            const { data, status } = yield call(templates.get, {});
            if (Array.isArray(data) && status === 200) {
              yield put(
                templatesHistory.templates.success({
                  templates: getSortedTemplateData(
                    data,
                    SortingOptions.LAST_UPDATED_DATE,
                    SortingOrder.DESC
                  ),
                  groups: groupByGroupId(
                    getSortedTemplateData(
                      data,
                      SortingOptions.LAST_UPDATED_DATE,
                      SortingOrder.DESC
                    )
                  ),
                })
              );
            }
          } else {
            const { data: templateDate, status } = yield call(
              templates.getById,
              {
                templateId: payload.templateId,
              }
            );
            if (status === 200) {
              yield put(templatesHistory.template.success(templateDate));
            }
          }
          $Message.destroy();
          if (payload?.IsDelete) {
             $MessageBox(
               "success",
               `US.USC.TEMPLATEEDITOR:MESSAGES.REMOVED_FROM_MESSAGE_TEMPLATE_SUCCESSFULLY`,
               "",
               ""
             )}
            else{
              $MessageBox(
                "success",
                `US.USC.TEMPLATEEDITOR:MESSAGES.ADDED_TO_MESSAGE_TEMPLATE_SUCCESSFULLY`,
                "",
                ""
              )
            }
          yield put(editorHome.messageTemplates.success(data));
        } else {
          $Message.destroy();
          if (payload?.IsDelete) {
            $MessageBox(
              "error",
              `US.USC.TEMPLATEEDITOR:MESSAGES.REMOVING_FROM_MESSAGE_TEMPLATE_FAILED`,
              "",
              ""
            )}
           else{
             $MessageBox(
               "error",
               `US.USC.TEMPLATEEDITOR:MESSAGES.ADDING_TO_MESSAGE_TEMPLATE_FAILED`,
               "",
               ""
             )
           }
          yield put(editorHome.messageTemplates.fail(data));
        }
      } catch (error) {
        $Message.destroy();
        $MessageBox(
          "error",
          `US.USC.TEMPLATEEDITOR:MESSAGES.UPDATE_TO_MESSAGE_TEMPLATE_FAILED`,
          "",
          ""
        )
        yield put(editorHome.messageTemplates.fail(error as any));
      }
    },
  },
  texts: {
    getAll: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(texts.get, params.groupName);
        if (Array.isArray(data) && status === 200) {
          yield put(editorHome.texts.success(data));
        } else {
          yield put(editorHome.texts.fail({}));
        }
      } catch (error) {
        yield put(editorHome.texts.fail(error as any));
      }
    },
  },
  category: {
    getAdapters: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(categoryDataAdapters.get, params);
        if (Array.isArray(data) && status === 200) {
          yield put(editorHome.dataAdapterList.success(data));
        } else {
          yield put(editorHome.dataAdapterList.success([]));
        }
      } catch (error) {
        yield put(editorHome.dataAdapterList.fail([]));
      }
    },
    save: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(category.save, params);
        if (data.categoryId > 0 && status === 201) {
          $MessageBox(
            "success",
            `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_CATEGORY_SAVED_SUCCESSFULLY`,
            "",
            ""
          );
          yield put(editorHome.templateCategory.success(data));
        } else {
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.ERROR_IN_SAVING_TEMPLATE_CATEGORY`,
            "",
            ""
          );
          yield put(editorHome.templateCategory.success({}));
        }
      } catch (error) {
        $MessageBox(
          "error",
          `US.USC.TEMPLATEEDITOR:MESSAGES.ERROR_IN_SAVING_TEMPLATE_CATEGORY`,
          "",
          ""
        );
        yield put(editorHome.templateCategory.fail({}));
      }
    },
    update: function* (action: any) {
      try {
        $Message.loading(i18n.t("COMMON.SAVING") as string, 0);
        const params = action?.payload.data ?? {};
        const { data: updateResponse, status: updateStatue } = yield call(
          category.update,
          params
        );
        if (updateStatue === 200) {
          const { data, status } = yield call(templates.get, params);
          $Message.destroy();
          if (Array.isArray(data) && status === 200) {
            yield put(
              templatesHistory.templates.success({
                templates: data,
                groups: groupByGroupId(data),
              })
            );
          } else {
            yield put(
              templatesHistory.templates.success({ templates: [], groups: [] })
            );
          }
          $MessageBox(
            "success",
            `US.USC.TEMPLATEEDITOR:MESSAGES.UPDATED_SUCCESSFULLY`,
            "",
            ""
          );
          yield put(editorHome.categoryDisplayName.success(updateResponse));
        } else {
          yield put(editorHome.categoryDisplayName.success({}));
        }
      } catch (error) {
        yield put(editorHome.categoryDisplayName.fail({}));
        $Message.destroy();
        $MessageBox(
          "success",
          `US.USC.TEMPLATEEDITOR:MESSAGES.UPDATE_FAILED`,
          "",
          ""
        );
      }
    },
    get: function* (action: any) {
      try {
        const params = action?.payload.data ?? {};
        const { data, status } = yield call(category.get, params);
        if (status === 200) {
          yield put(editorHome.categoryDetails.success(data));
        } else {
          yield put(editorHome.categoryDetails.success({}));
        }
      } catch (error) {
        yield put(editorHome.categoryDetails.fail({}));
      }
    },
  },
  settings: {
    getAll: function* (action: any) {
      try {
        const { data, status } = yield call(
          settings.getAll,
          action.payload?.data
        );
        if (status === 200) {
          yield put(editorHome.settings.success(data));
        } else {
          yield put(editorHome.settings.fail([]));
        }
      } catch (error) {
        yield put(editorHome.settings.fail(error as any));
      }
    },
    update: function* (action: any) {
      try {
        $Message.loading(
          i18n.t("US.USC.TEMPLATEEDITOR:MESSAGES.SETTINGS_UPDATING") as string,
          0
        );
        const { data, status } = yield call(
          settings.update,
          action.payload?.data
        );
        $Message.destroy();
        if (status === 200) {
          yield put(editorHome.settings.success(data));
          $MessageBox(
            "success",
            `US.USC.TEMPLATEEDITOR:MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY`,
            "",
            ""
          );
        } else if (status === 401) {
          yield put(editorHome.settings.fail(data));
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.YOU_ARE_NOT_ALLOWED_TO_UPDATE_THE_SETTINGS`,
            "",
            ""
          );
        } else {
          yield put(editorHome.settings.fail(data));
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.SETTINGS_UPDATE_FAILED`,
            "",
            ""
          );
        }
      } catch (error) {
        yield put(editorHome.settings.fail(error as any));
        $MessageBox(
          "error",
          `US.USC.TEMPLATEEDITOR:MESSAGES.SETTINGS_UPDATE_FAILED`,
          "",
          ""
        );
      }
    },
  },
};

export default [
  takeLatest(GET_ALL_TEMPLATE_RULES, editorHomeSagas.template.getAllRules),
  takeLatest(GET_ALL_SETTINGS, editorHomeSagas.settings.getAll),
  takeLatest(UPDATE_SETTINGS, editorHomeSagas.settings.update),
  takeLatest(GET_ALL_TEXT_DATA, editorHomeSagas.texts.getAll),
  takeLatest(GET_DATA_ADAPTERS, editorHomeSagas.category.getAdapters),
  takeLatest(SAVE_TEMPLATE_CATEGORY, editorHomeSagas.category.save),
  takeLatest(UPDATE_CATEGORY_DISPLAY_NAME, editorHomeSagas.category.update),
  takeLatest(GET_CATEGORY_DETAILS, editorHomeSagas.category.get),
  takeLatest(
    ADD_REMOVE_FROM_MESSAGE_TEMPLATES,
    editorHomeSagas.template.addRemoveFromMessageTemplates
  ),
];
